import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
} from 'react';
import {
  isoDateTime,
  money,
  percent,
  type CustomFieldDefinition,
  type Task,
  type TaskId,
} from '@bigint-project/core';
import type {
  CellCoord,
  ColumnDef,
  ManualTaskCustomFieldKind,
  TaskGridProps,
} from './types.js';
import { formatCellValue } from './columns.js';
import { parseDurationInput } from './duration.js';
import { formatDateDisplay, parseDateInput } from './date-format.js';
import { buildDependenciesFromInput, formatTaskPredecessorsDisplay } from './dependency-column.js';
import { getVisibleTasks } from './visibility.js';
import { buildRangeTsv, cellDisplayText, isCellInRange, isSingleCell, normalizeRange } from './cell-range.js';
import { PredecessorPicker } from './PredecessorPicker.js';
import { progressStatusTextClass } from '../progress/format.js';
import {
  buildPasteImport,
  looksLikeMsProjectExport,
  splitClipboardGrid,
  summarizePasteWarnings,
} from './paste-import.js';
import { ContextMenu, type ContextMenuState } from './ContextMenu.js';
import { ColumnSettingsPanel } from './ColumnSettingsPanel.js';

const OVERSCAN_ROWS = 6;
const FALLBACK_VIEWPORT_ROWS = 20;
const MIN_COLUMN_WIDTH = 40;
const DEFAULT_ROW_HEIGHT = 24;
const EMPTY_CUSTOM_FIELDS: readonly CustomFieldDefinition[] = [];
const MANUAL_TASK_CUSTOM_FIELD_KINDS: readonly ManualTaskCustomFieldKind[] = [
  'Text',
  'Number',
  'Cost',
  'Date',
  'Flag',
];

/**
 * 排程模式圖示欄的寬度。
 *
 * 刻意做成「欄位清單之外」的固定寬度左側槽,而不是塞進 `columns`:
 * 一旦成為真正的欄位就會改變所有 colIndex,連帶影響選取範圍、複製貼上與欄位設定;
 * 它也不是使用者可隱藏/調寬的資料欄,而是列的狀態標記。
 */
const SCHEDULE_GUTTER_WIDTH = 18;

/** 手動排程標記(圖釘)。不用文字欄位以免佔用寬度;`title` 提供無障礙說明。 */
function ManualScheduleMark(): React.JSX.Element {
  return (
    <svg
      viewBox="0 0 12 12"
      width={11}
      height={11}
      aria-hidden="true"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.3}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M7.7 1.1 10.9 4.3" />
      <path d="M8.4 1.8 6.3 3.9 3.3 4.6l4.1 4.1.7-3 2.1-2.1" />
      <path d="M4.7 7.3 1.5 10.5" />
    </svg>
  );
}

interface EditingState {
  /**
   * 用 TaskId + 欄位 id 定位,而非 row/column index。
   * 編輯過程中若發生收合、列重排或欄位重排,index 會失效並指向錯誤的任務/欄位;
   * id 在整個編輯期間保持穩定。
   */
  readonly taskId: TaskId;
  readonly columnId: string;
  draft: string;
}

interface ClipboardCell {
  readonly kind: ColumnDef['kind'];
  readonly text: string;
}

function applyDatePart(previous: unknown, datePart: string): string {
  const timePart = typeof previous === 'string' && previous.includes('T')
    ? previous.slice(11)
    : '08:00:00';
  return `${datePart}T${timePart}`;
}

/**
 * 日期文字正規化為 yyyy-mm-dd。
 *
 * 手動編輯在 commitEdit 就已經擋掉非法格式(見 isDateColumnInputValid),這裡的
 * fallback 是給**貼上路徑**用的:外部剪貼簿的日期格式寬鬆(paste-import 支援
 * mm-dd-yy 等),不因為不符合顯示格式就整格丟棄,維持原本行為。
 */
function normalizeDateText(text: string): string {
  return parseDateInput(text) ?? formatDateDisplay(text);
}

function isManualTaskCustomField(
  field: CustomFieldDefinition,
): field is CustomFieldDefinition & { readonly kind: ManualTaskCustomFieldKind } {
  return field.scope === 'Task'
    && field.formula === null
    && field.rollup === 'None'
    && MANUAL_TASK_CUSTOM_FIELD_KINDS.includes(field.kind as ManualTaskCustomFieldKind);
}

function resolveCustomField(
  column: ColumnDef,
  fieldsById: ReadonlyMap<string, CustomFieldDefinition>,
): (CustomFieldDefinition & { readonly kind: ManualTaskCustomFieldKind }) | null {
  if (!column.custom || !column.customFieldId || !column.customFieldKind) return null;
  const field = fieldsById.get(column.customFieldId);
  if (!field || !isManualTaskCustomField(field) || field.kind !== column.customFieldKind) return null;
  return field;
}

function lookupOptionValue(value: string | number | boolean | null): string {
  return JSON.stringify([typeof value, value]);
}

function draftForCustomField(
  task: Task,
  field: CustomFieldDefinition & { readonly kind: ManualTaskCustomFieldKind },
): string {
  const values = task.customFieldValues;
  const value = values && Object.prototype.hasOwnProperty.call(values, field.id)
    ? values[field.id] ?? null
    : field.defaultValue;
  if (value === null) return '';
  const lookup = field.lookup?.find((item) => Object.is(item.value, value));
  return lookup ? lookupOptionValue(lookup.value) : String(value);
}

type CustomFieldParseResult =
  | { readonly ok: true; readonly value: string | number | boolean | null }
  | { readonly ok: false; readonly error: string };

function parseCustomFieldValue(
  text: string,
  field: CustomFieldDefinition & { readonly kind: ManualTaskCustomFieldKind },
): CustomFieldParseResult {
  const trimmed = text.trim();
  if (trimmed === '') return { ok: true, value: null };

  if (field.lookup !== null) {
    const match = field.lookup.find(
      (item) => lookupOptionValue(item.value) === text || item.label === text,
    );
    return match
      ? { ok: true, value: match.value }
      : { ok: false, error: `請從「${field.name}」的選項中選擇值。` };
  }

  if (field.kind === 'Text') return { ok: true, value: text };
  if (field.kind === 'Number' || field.kind === 'Cost') {
    const value = Number(trimmed);
    return Number.isFinite(value)
      ? { ok: true, value }
      : { ok: false, error: `「${field.name}」必須是數字。` };
  }
  if (field.kind === 'Date') {
    const value = parseDateInput(trimmed);
    return value
      ? { ok: true, value }
      : { ok: false, error: `「${field.name}」日期格式須為 yyyy-mm-dd。` };
  }
  const normalized = trimmed.toLowerCase();
  if (normalized === 'true') return { ok: true, value: true };
  if (normalized === 'false') return { ok: true, value: false };
  return { ok: false, error: `「${field.name}」必須是 true 或 false。` };
}

/**
 * 依欄位型別,把使用者輸入的文字轉成要寫回 Task 的 patch。
 *
 * 'dependency-list' 欄位不寫回 Task(它沒有對應欄位,依賴關係是 Project 層級的
 * 獨立資料),一律回傳 null —— 呼叫端(commitEdit / pasteSelection)必須在呼叫本
 * 函式之前就用 buildDependenciesFromInput 走 onDependencyChange 這條路徑,不能落到
 * 這裡。
 */
function buildPatchForColumn(task: Task, column: ColumnDef, text: string): Partial<Task> | null {
  if (
    column.kind === 'dependency-list'
    || column.kind === 'resource-list'
    || column.kind === 'expected-percent'
    || column.kind === 'progress-status'
  ) return null;
  if (column.custom) return null;

  switch (column.field) {
    case 'name':
      return { name: text };
    case 'durationMinutes': {
      const parsed = parseDurationInput(text);
      if (parsed === null) return null;
      return { durationMinutes: parsed };
    }
    case 'start':
      return { start: isoDateTime(applyDatePart(task.start, normalizeDateText(text))) };
    case 'finish':
      return { finish: isoDateTime(applyDatePart(task.finish, normalizeDateText(text))) };
    case 'percentComplete': {
      const num = Number(text);
      if (!Number.isFinite(num)) return null;
      return { percentComplete: percent(Math.min(100, Math.max(0, num))) };
    }
    case 'fixedCost': {
      const num = Number(text);
      if (!Number.isFinite(num)) return null;
      return { fixedCost: money(num) };
    }
    case 'totalFloat': {
      const parsed = parseDurationInput(text);
      if (parsed === null) return null;
      return { totalFloat: parsed };
    }
    default:
      return null;
  }
}

export function TaskGrid(props: TaskGridProps): React.JSX.Element {
  const {
    tasks,
    columns,
    collapsedTaskIds,
    selectedTaskIds,
    dependencies,
    resourceNamesByTask,
    expectedProgressByTask,
    availableColumns,
    rowHeight,
    scrollTop,
    onScrollTopChange,
    onTaskChange,
    customFields = EMPTY_CUSTOM_FIELDS,
    onCustomFieldChange,
    onTaskInsert,
    onTaskDelete,
    onIndent,
    onOutdent,
    onToggleCollapse,
    onTaskSelect,
    onColumnsChange,
    onDependencyChange,
    onInsertMilestone,
    onInsertSummary,
    onTasksPaste,
  } = props;

  const bodyRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const clipboardRef = useRef<ClipboardCell[][] | null>(null);
  /** 內部複製時同步寫出的純文字;用來辨識 native paste 事件是否就是這份內容。 */
  const clipboardTextRef = useRef<string | null>(null);
  const resizeStateRef = useRef<{ colIndex: number; startX: number; startWidth: number } | null>(
    null,
  );
  /** 滑鼠拖曳選取範圍進行中(mousedown ~ mouseup);用 ref 避免每次移動都重繪整表。 */
  const draggingRef = useRef(false);

  const [viewportHeight, setViewportHeight] = useState(0);
  const [activeCell, setActiveCell] = useState<CellCoord | null>(null);
  const [selectionAnchor, setSelectionAnchor] = useState<CellCoord | null>(null);
  const [editing, setEditing] = useState<EditingState | null>(null);
  /** 前置任務編輯/貼上失敗時的錯誤訊息(找不到 ref、自我參照、循環相依等)。 */
  const [editError, setEditError] = useState<string | null>(null);
  const [contextMenu, setContextMenu] = useState<ContextMenuState | null>(null);
  const [showColumnSettings, setShowColumnSettings] = useState(false);
  /** 前置任務下拉勾選面板(null 代表未開啟)。 */
  const [picker, setPicker] = useState<{ taskId: TaskId; top: number; left: number } | null>(null);
  const [pickerError, setPickerError] = useState<string | null>(null);

  const visibleTasks = useMemo(
    () => getVisibleTasks(tasks, collapsedTaskIds),
    [tasks, collapsedTaskIds],
  );
  const selectedSet = useMemo(() => new Set<TaskId>(selectedTaskIds), [selectedTaskIds]);
  const customFieldsById = useMemo(
    () => new Map(customFields.filter(isManualTaskCustomField).map((field) => [field.id as string, field])),
    [customFields],
  );

  const isColumnEditable = useCallback(
    (column: ColumnDef): boolean => (
      column.editable
      && (!column.custom || (onCustomFieldChange !== undefined && resolveCustomField(column, customFieldsById) !== null))
    ),
    [customFieldsById, onCustomFieldChange],
  );

  // --- 視窗高度量測(供虛擬捲動計算可視列數) ---
  useEffect(() => {
    const el = bodyRef.current;
    if (!el) return undefined;
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) setViewportHeight(entry.contentRect.height);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // --- 受控 scrollTop:外部(甘特圖)驅動時同步捲動位置 ---
  useEffect(() => {
    if (
      Number.isFinite(scrollTop) &&
      bodyRef.current &&
      Math.abs(bodyRef.current.scrollTop - scrollTop) > 0.5
    ) {
      bodyRef.current.scrollTop = scrollTop;
    }
  }, [scrollTop]);

  // 防呆:非正數 / 非有限的 rowHeight 會讓後續除法產生 NaN 或無限索引,
  // 過大或負值的受控 scrollTop 會讓切片範圍落在清單之外而渲染出空白。
  const safeRowHeight = Number.isFinite(rowHeight) && rowHeight > 0 ? rowHeight : DEFAULT_ROW_HEIGHT;
  const totalHeight = visibleTasks.length * safeRowHeight;
  const safeScrollTop = Number.isFinite(scrollTop) ? Math.min(Math.max(0, scrollTop), totalHeight) : 0;

  const effectiveViewportHeight = viewportHeight || FALLBACK_VIEWPORT_ROWS * safeRowHeight;
  const startIndex = Math.max(0, Math.floor(safeScrollTop / safeRowHeight) - OVERSCAN_ROWS);
  const visibleRowCount = Math.ceil(effectiveViewportHeight / safeRowHeight) + OVERSCAN_ROWS * 2;
  const endIndex = Math.min(visibleTasks.length, startIndex + visibleRowCount);

  const renderedRows = useMemo(
    () => visibleTasks.slice(startIndex, endIndex).map((task, offset) => ({
      task,
      rowIndex: startIndex + offset,
    })),
    [visibleTasks, startIndex, endIndex],
  );

  const totalColumnsWidth = useMemo(
    () => columns.reduce((sum, column) => sum + column.width, SCHEDULE_GUTTER_WIDTH),
    [columns],
  );

  const handleBodyScroll = useCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      const target = event.currentTarget;
      if (headerRef.current) headerRef.current.scrollLeft = target.scrollLeft;
      onScrollTopChange(target.scrollTop);
    },
    [onScrollTopChange],
  );

  const closeMenus = useCallback(() => {
    setContextMenu(null);
  }, []);

  // ---------------------------------------------------------------------
  // 編輯
  // ---------------------------------------------------------------------

  const commitEdit = useCallback(
    (moveAfter: 'down' | 'right' | 'left' | 'none' = 'none') => {
      if (!editing) return;
      // 用 id 在「當下」的 tasks / columns 重新查找,即使編輯期間發生收合(任務仍在
      // 完整清單中,只是暫時不可見)或欄位重排/移除,依然能落在正確的任務與欄位上。
      const task = tasks.find((t) => t.id === editing.taskId);
      const column = columns.find((c) => c.id === editing.columnId);

      if (!task || !column || !isColumnEditable(column)) {
        setEditing(null);
        setEditError(null);
        return;
      }

      const customField = resolveCustomField(column, customFieldsById);
      if (customField) {
        const parsed = parseCustomFieldValue(editing.draft, customField);
        if (!parsed.ok) {
          setEditError(parsed.error);
          return;
        }
        onCustomFieldChange?.(task.id, customField.id, parsed.value);
      } else if (column.kind === 'dependency-list') {
        const result = buildDependenciesFromInput(editing.draft, task.id, tasks, dependencies);
        if (!result.ok) {
          // 找不到 ref / 自我參照 / 明顯循環:不落地,保留編輯狀態與草稿,顯示錯誤,
          // 讓使用者修正 —— 不可靜默忽略。
          setEditError(result.error);
          return;
        }
        onDependencyChange(task.id, result.dependencies);
      } else if (!column.custom && column.kind === 'date' && parseDateInput(editing.draft) === null) {
        // 手動編輯的日期只接受 yyyy-mm-dd / yyyy-mm-dd 的等價寫法;不可靜默吞掉,
        // 否則會把無法解析的字串寫成錯誤的日期。(貼上路徑另有寬鬆解析,見 normalizeDateText)
        setEditError('日期格式須為 yyyy-mm-dd(例如 2026-07-24)');
        return;
      } else {
        const patch = buildPatchForColumn(task, column, editing.draft);
        if (patch) onTaskChange(task.id, patch);
      }

      setEditError(null);
      setEditing(null);

      if (moveAfter === 'down') {
        setActiveCell((cell) =>
          cell ? { rowIndex: Math.min(visibleTasks.length - 1, cell.rowIndex + 1), colIndex: cell.colIndex } : cell,
        );
      } else if (moveAfter === 'right') {
        setActiveCell((cell) =>
          cell ? { rowIndex: cell.rowIndex, colIndex: Math.min(columns.length - 1, cell.colIndex + 1) } : cell,
        );
      } else if (moveAfter === 'left') {
        setActiveCell((cell) =>
          cell ? { rowIndex: cell.rowIndex, colIndex: Math.max(0, cell.colIndex - 1) } : cell,
        );
      }
    },
    [columns, customFieldsById, dependencies, editing, isColumnEditable, onCustomFieldChange, onDependencyChange, onTaskChange, tasks, visibleTasks],
  );

  const cancelEdit = useCallback(() => {
    setEditing(null);
    setEditError(null);
  }, []);

  const startEditing = useCallback(
    (rowIndex: number, colIndex: number, initialDraft?: string) => {
      const task = visibleTasks[rowIndex];
      const column = columns[colIndex];
      if (!task || !column || !isColumnEditable(column)) return;
      const customField = resolveCustomField(column, customFieldsById);
      const draft =
        initialDraft ??
        (customField
          ? draftForCustomField(task, customField)
          : column.kind === 'dependency-list'
          ? formatTaskPredecessorsDisplay(task.id, dependencies, tasks)
          : formatCellValue(task, column));
      setEditError(null);
      setEditing({ taskId: task.id, columnId: column.id, draft });
    },
    [columns, customFieldsById, dependencies, isColumnEditable, tasks, visibleTasks],
  );

  // ---------------------------------------------------------------------
  // 選取 / 導覽
  // ---------------------------------------------------------------------

  /**
   * 目前的儲存格矩形選取範圍(anchor = selectionAnchor,focus = activeCell)。
   *
   * 與整列選取(props.selectedTaskIds)是兩套並存的模型:整列選取是跨元件共享狀態
   * (甘特圖靠它高亮),儲存格範圍是本元件內部狀態(複製/貼上的作用區域)。
   * 兩者由同一組操作同時驅動(見 syncRowSelection),因此範圍涵蓋的列必定也是被選中的
   * 列,不會出現「表格選了 5 列、甘特圖只亮 1 列」。
   */
  const selectionRange = useMemo(
    () => (activeCell && selectionAnchor ? normalizeRange(selectionAnchor, activeCell) : null),
    [activeCell, selectionAnchor],
  );

  /**
   * 把儲存格範圍涵蓋的列同步成整列選取。
   *
   * 先以 anchor 列送 'single' 重設,再以 focus 列送 'range' —— 上層(GanttSplitView /
   * demo)的 'range' 是以「目前選取的最後一筆」為起點,先重設才能保證每次都從 anchor
   * 算起,不會被上一次的殘留選取影響。
   */
  const syncRowSelection = useCallback(
    (anchorRowIndex: number, focusRowIndex: number) => {
      const anchorTask = visibleTasks[anchorRowIndex];
      if (!anchorTask) return;
      onTaskSelect(anchorTask.id, 'single');
      if (focusRowIndex === anchorRowIndex) return;
      const focusTask = visibleTasks[focusRowIndex];
      if (focusTask) onTaskSelect(focusTask.id, 'range');
    },
    [onTaskSelect, visibleTasks],
  );

  /** 移動作用格(不按 Shift 時同時重設 anchor),並同步整列選取。 */
  const moveActiveCell = useCallback(
    (next: CellCoord, extend: boolean) => {
      setActiveCell(next);
      const anchor = extend && selectionAnchor ? selectionAnchor : next;
      if (!extend) setSelectionAnchor(next);
      syncRowSelection(anchor.rowIndex, next.rowIndex);
    },
    [selectionAnchor, syncRowSelection],
  );

  const handleCellMouseDown = useCallback(
    (event: ReactMouseEvent, rowIndex: number, colIndex: number) => {
      const task = visibleTasks[rowIndex];
      if (!task) return;

      if (event.shiftKey) {
        // Shift+點擊:延伸既有範圍(保留 anchor),Excel 語意。
        moveActiveCell({ rowIndex, colIndex }, true);
        return;
      }
      if (event.metaKey || event.ctrlKey) {
        // Cmd/Ctrl+點擊:整列的加選/減選;儲存格範圍收斂回單一格。
        setActiveCell({ rowIndex, colIndex });
        setSelectionAnchor({ rowIndex, colIndex });
        onTaskSelect(task.id, 'additive');
        return;
      }

      setActiveCell({ rowIndex, colIndex });
      setSelectionAnchor({ rowIndex, colIndex });
      onTaskSelect(task.id, 'single');

      // 開始拖曳選取:mouseup 前經過的儲存格會持續延伸範圍(見 handleCellMouseEnter)。
      draggingRef.current = true;
      const stopDragging = (): void => {
        draggingRef.current = false;
        window.removeEventListener('mouseup', stopDragging);
      };
      window.addEventListener('mouseup', stopDragging);
    },
    [moveActiveCell, onTaskSelect, visibleTasks],
  );

  const handleCellMouseEnter = useCallback(
    (rowIndex: number, colIndex: number) => {
      if (!draggingRef.current) return;
      moveActiveCell({ rowIndex, colIndex }, true);
    },
    [moveActiveCell],
  );

  const selectAllCells = useCallback(() => {
    if (visibleTasks.length === 0 || columns.length === 0) return;
    const anchor: CellCoord = { rowIndex: 0, colIndex: 0 };
    const focus: CellCoord = { rowIndex: visibleTasks.length - 1, colIndex: columns.length - 1 };
    setSelectionAnchor(anchor);
    setActiveCell(focus);
    syncRowSelection(anchor.rowIndex, focus.rowIndex);
  }, [columns.length, syncRowSelection, visibleTasks.length]);

  const clampRow = (index: number): number => Math.min(Math.max(index, 0), Math.max(0, visibleTasks.length - 1));
  const clampCol = (index: number): number => Math.min(Math.max(index, 0), Math.max(0, columns.length - 1));

  // ---------------------------------------------------------------------
  // 前置任務下拉勾選
  // ---------------------------------------------------------------------

  const openPicker = useCallback((taskId: TaskId, cellElement: HTMLElement | null) => {
    const rootRect = rootRef.current?.getBoundingClientRect();
    const cellRect = cellElement?.getBoundingClientRect();
    const top = rootRect && cellRect ? cellRect.bottom - rootRect.top : 0;
    const left = rootRect && cellRect ? Math.max(0, Math.min(cellRect.left - rootRect.left, rootRect.width - 380)) : 0;
    setPickerError(null);
    setPicker({ taskId, top, left });
  }, []);

  /**
   * 下拉勾選的落地:把面板組出的文字語法丟進與手動輸入**完全相同**的解析路徑,
   * 兩種輸入方式因此不可能產生不一致的結果。
   */
  const commitPicker = useCallback(
    (taskId: TaskId, input: string) => {
      const task = tasks.find((t) => t.id === taskId);
      if (!task) {
        setPicker(null);
        return;
      }
      const result = buildDependenciesFromInput(input, taskId, tasks, dependencies);
      if (!result.ok) {
        setPickerError(result.error);
        return;
      }
      onDependencyChange(taskId, result.dependencies);
      setPickerError(null);
      setPicker(null);
    },
    [dependencies, onDependencyChange, tasks],
  );

  // ---------------------------------------------------------------------
  // 複製 / 貼上
  // ---------------------------------------------------------------------

  const copySelection = useCallback(() => {
    if (!selectionRange) return;
    const { rowStart, rowEnd, colStart, colEnd } = selectionRange;

    const grid: ClipboardCell[][] = [];
    for (let r = rowStart; r <= rowEnd; r += 1) {
      const task = visibleTasks[r];
      if (!task) continue;
      const rowCells: ClipboardCell[] = [];
      for (let c = colStart; c <= colEnd; c += 1) {
        const column = columns[c];
        if (!column) continue;
        const text = cellDisplayText(task, column, dependencies, tasks, resourceNamesByTask, expectedProgressByTask);
        rowCells.push({ kind: column.kind, text });
      }
      grid.push(rowCells);
    }
    clipboardRef.current = grid;

    // 寫到系統剪貼簿的是「格式化後的顯示值」TSV(含必要的引號轉義),
    // 使用者貼到 Excel 看到的與畫面完全一致,不是內部數值。
    const text = buildRangeTsv(selectionRange, visibleTasks, columns, dependencies, tasks, resourceNamesByTask, expectedProgressByTask);
    clipboardTextRef.current = text;
    if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(text).catch(() => undefined);
    }
    return text;
  }, [columns, dependencies, selectionRange, tasks, visibleTasks]);

  /**
   * 把二維文字格點貼到以 activeCell 為左上角的**既有列**(不新增列)。
   * 內部 Cmd+C/Cmd+V 與「非 WBS 匯出表」的外部貼上都走這條路徑。
   */
  const pasteTextGrid = useCallback((grid: readonly (readonly string[])[]) => {
    if (!activeCell) return;

    // 前置任務欄位找不到 ref 等錯誤不可靜默吞掉,但整批貼上又不宜因單一儲存格
    // 失敗就整個中止 —— 這裡保留最後一個錯誤訊息,貼上結束後統一顯示一次。
    let pasteError: string | null = null;
    let filledRows = 0;

    for (let r = 0; r < grid.length; r += 1) {
      const targetTask = visibleTasks[activeCell.rowIndex + r];
      if (!targetTask) {
        pasteError = `已貼上 ${filledRows} 列,剩餘 ${grid.length - r} 列超出現有任務列數而未貼上`;
        break;
      }
      filledRows += 1;
      const rowCells = grid[r];
      if (!rowCells) continue;

      // 內建欄位仍合併成一份 Task patch；自訂欄位則逐格交給專屬 callback，
      // 讓上層 command 依 definition 做唯一的資料完整性判斷，絕不可直接
      // 組寫 `task.customFieldValues`。
      let mergedPatch: Partial<Task> = {};

      for (let c = 0; c < rowCells.length; c += 1) {
        const targetColumn = columns[activeCell.colIndex + c];
        if (!targetColumn || !isColumnEditable(targetColumn)) continue;
        const cellText = rowCells[c];
        if (cellText === undefined) continue;

        if (targetColumn.kind === 'dependency-list') {
          // 依賴關係走獨立的 onDependencyChange,不併入 Task patch。
          const result = buildDependenciesFromInput(cellText, targetTask.id, tasks, dependencies);
          if (!result.ok) {
            pasteError = result.error;
            continue;
          }
          onDependencyChange(targetTask.id, result.dependencies);
          continue;
        }

        const customField = resolveCustomField(targetColumn, customFieldsById);
        if (customField) {
          const parsed = parseCustomFieldValue(cellText, customField);
          if (!parsed.ok) {
            pasteError = parsed.error;
            continue;
          }
          onCustomFieldChange?.(targetTask.id, customField.id, parsed.value);
          continue;
        }

        const patch = buildPatchForColumn(targetTask, targetColumn, cellText);
        if (!patch) continue;
        mergedPatch = { ...mergedPatch, ...patch };
      }

      if (Object.keys(mergedPatch).length > 0) {
        onTaskChange(targetTask.id, mergedPatch);
      }
    }

    setEditError(pasteError);
  }, [activeCell, columns, customFieldsById, dependencies, isColumnEditable, onCustomFieldChange, onDependencyChange, onTaskChange, tasks, visibleTasks]);

  const pasteSelection = useCallback(() => {
    const grid = clipboardRef.current;
    if (!grid) return;
    pasteTextGrid(grid.map((row) => row.map((cell) => cell.text)));
  }, [pasteTextGrid]);

  /**
   * 外部剪貼簿(Excel / MS Project 匯出表)貼上。
   *
   * 判定為 WBS 匯出表且上層提供 onTasksPaste 時,走「兩階段匯入」:
   * 先解析出所有列,再解析前置任務 ref,最後一次性交給上層建立(可新增列)。
   * 否則退回一般的儲存格填值行為。
   */
  const handlePaste = useCallback(
    (event: React.ClipboardEvent<HTMLDivElement>) => {
      // 正在儲存格編輯中時交給 input 自己處理。
      if (editing) return;
      const text = event.clipboardData?.getData('text/plain') ?? '';
      if (text.trim() === '') return;
      // 這份內容就是本表格剛剛複製出去的 → 已由 Cmd+V 的內部路徑處理,不重複套用。
      if (clipboardTextRef.current !== null && clipboardTextRef.current === text) return;

      event.preventDefault();
      const grid = splitClipboardGrid(text);

      if (onTasksPaste && looksLikeMsProjectExport(grid)) {
        const anchorTask = activeCell ? visibleTasks[activeCell.rowIndex] : undefined;
        const anchorIndex = anchorTask ? tasks.findIndex((t) => t.id === anchorTask.id) : -1;
        const startIndex = anchorIndex >= 0 ? anchorIndex : 0;
        const result = buildPasteImport(text, tasks, startIndex);
        onTasksPaste(result);
        setEditError(summarizePasteWarnings(result.tasks.length, result.warnings));
        return;
      }

      pasteTextGrid(grid);
    },
    [activeCell, editing, onTasksPaste, pasteTextGrid, tasks, visibleTasks],
  );

  // ---------------------------------------------------------------------
  // 鍵盤
  // ---------------------------------------------------------------------

  const handleGridKeyDown = useCallback(
    (event: ReactKeyboardEvent<HTMLDivElement>) => {
      if (editing) {
        if (event.key === 'Enter') {
          event.preventDefault();
          commitEdit('down');
        } else if (event.key === 'Tab') {
          event.preventDefault();
          commitEdit(event.shiftKey ? 'left' : 'right');
        } else if (event.key === 'Escape') {
          event.preventDefault();
          cancelEdit();
        }
        return;
      }

      if (!activeCell) return;
      const currentTask = visibleTasks[activeCell.rowIndex];
      const isMeta = event.metaKey || event.ctrlKey;

      if (isMeta && event.key.toLowerCase() === 'c') {
        event.preventDefault();
        copySelection();
        return;
      }
      if (isMeta && event.key.toLowerCase() === 'v') {
        event.preventDefault();
        pasteSelection();
        return;
      }
      if (isMeta && event.key.toLowerCase() === 'a') {
        event.preventDefault();
        selectAllCells();
        return;
      }

      switch (event.key) {
        case 'ArrowDown': {
          event.preventDefault();
          // Alt+↓ 在前置任務欄開啟下拉勾選(對應 Excel 的下拉開啟慣例)。
          const currentColumn = columns[activeCell.colIndex];
          if (event.altKey && currentColumn?.kind === 'dependency-list' && currentTask) {
            openPicker(
              currentTask.id,
              rootRef.current?.querySelector<HTMLElement>(
                `[data-testid="cell-${currentTask.id}-${currentColumn.id}"]`,
              ) ?? null,
            );
            break;
          }
          moveActiveCell(
            { rowIndex: clampRow(activeCell.rowIndex + 1), colIndex: activeCell.colIndex },
            event.shiftKey,
          );
          break;
        }
        case 'ArrowUp': {
          event.preventDefault();
          moveActiveCell(
            { rowIndex: clampRow(activeCell.rowIndex - 1), colIndex: activeCell.colIndex },
            event.shiftKey,
          );
          break;
        }
        case 'ArrowLeft': {
          event.preventDefault();
          moveActiveCell(
            { rowIndex: activeCell.rowIndex, colIndex: clampCol(activeCell.colIndex - 1) },
            event.shiftKey,
          );
          break;
        }
        case 'ArrowRight': {
          event.preventDefault();
          moveActiveCell(
            { rowIndex: activeCell.rowIndex, colIndex: clampCol(activeCell.colIndex + 1) },
            event.shiftKey,
          );
          break;
        }
        case 'Enter': {
          event.preventDefault();
          startEditing(activeCell.rowIndex, activeCell.colIndex);
          break;
        }
        case 'Tab': {
          // 一律攔截,避免焦點跳出表格；只有在「列首」(第 0 欄)才做縮排/升階,
          // 其餘欄位維持一般的左右導覽。
          event.preventDefault();
          if (activeCell.colIndex === 0) {
            if (event.shiftKey) {
              onOutdent(selectedTaskIds.length > 0 ? selectedTaskIds : currentTask ? [currentTask.id] : []);
            } else {
              onIndent(selectedTaskIds.length > 0 ? selectedTaskIds : currentTask ? [currentTask.id] : []);
            }
          } else {
            moveActiveCell(
              {
                rowIndex: activeCell.rowIndex,
                colIndex: event.shiftKey
                  ? clampCol(activeCell.colIndex - 1)
                  : clampCol(activeCell.colIndex + 1),
              },
              false,
            );
          }
          break;
        }
        case 'Delete':
        case 'Backspace': {
          event.preventDefault();
          if (selectedTaskIds.length > 0) {
            onTaskDelete(selectedTaskIds);
          } else if (currentTask) {
            onTaskDelete([currentTask.id]);
          }
          break;
        }
        case 'Insert': {
          event.preventDefault();
          onTaskInsert(currentTask ? currentTask.id : null);
          break;
        }
        default: {
          if (event.key.length === 1 && !isMeta && !event.altKey) {
            startEditing(activeCell.rowIndex, activeCell.colIndex, event.key);
          }
          break;
        }
      }
    },
    [
      activeCell,
      cancelEdit,
      columns,
      commitEdit,
      copySelection,
      editing,
      moveActiveCell,
      onIndent,
      onOutdent,
      onTaskDelete,
      onTaskInsert,
      openPicker,
      pasteSelection,
      selectAllCells,
      selectedTaskIds,
      startEditing,
      visibleTasks,
    ],
  );

  // ---------------------------------------------------------------------
  // 欄寬調整
  // ---------------------------------------------------------------------

  const handleResizeMouseDown = useCallback(
    (event: ReactMouseEvent, colIndex: number) => {
      event.preventDefault();
      event.stopPropagation();
      const column = columns[colIndex];
      if (!column) return;
      resizeStateRef.current = { colIndex, startX: event.clientX, startWidth: column.width };

      const handleMouseMove = (moveEvent: MouseEvent): void => {
        const resizing = resizeStateRef.current;
        if (!resizing) return;
        const delta = moveEvent.clientX - resizing.startX;
        const newWidth = Math.max(MIN_COLUMN_WIDTH, resizing.startWidth + delta);
        const next = columns.map((c, i) => (i === resizing.colIndex ? { ...c, width: newWidth } : c));
        onColumnsChange(next);
      };
      const handleMouseUp = (): void => {
        resizeStateRef.current = null;
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    },
    [columns, onColumnsChange],
  );

  // ---------------------------------------------------------------------
  // 右鍵選單
  // ---------------------------------------------------------------------

  const openContextMenu = useCallback(
    (event: ReactMouseEvent, task: Task | null) => {
      event.preventDefault();
      const targetIds = task && selectedSet.has(task.id) && selectedTaskIds.length > 0
        ? selectedTaskIds
        : task
          ? [task.id]
          : [];

      setContextMenu({
        x: event.clientX,
        y: event.clientY,
        items: [
          { id: 'insert-task', label: '新增任務', onSelect: () => onTaskInsert(task ? task.id : null) },
          {
            id: 'insert-milestone',
            label: '插入里程碑',
            onSelect: () => (onInsertMilestone ?? onTaskInsert)(task ? task.id : null),
          },
          {
            id: 'insert-summary',
            label: '插入摘要任務',
            onSelect: () => (onInsertSummary ?? onTaskInsert)(task ? task.id : null),
          },
          {
            id: 'delete',
            label: '刪除',
            disabled: targetIds.length === 0,
            onSelect: () => onTaskDelete(targetIds),
            separatorBefore: true,
          },
          { id: 'indent', label: '縮排', disabled: targetIds.length === 0, onSelect: () => onIndent(targetIds) },
          { id: 'outdent', label: '升階', disabled: targetIds.length === 0, onSelect: () => onOutdent(targetIds) },
          {
            id: 'column-settings',
            label: '欄位設定',
            onSelect: () => setShowColumnSettings(true),
            separatorBefore: true,
          },
        ],
      });
    },
    [onIndent, onInsertMilestone, onInsertSummary, onOutdent, onTaskDelete, onTaskInsert, selectedSet, selectedTaskIds],
  );

  return (
    <div
      ref={rootRef}
      className="relative flex h-full w-full flex-col bg-surface text-sm text-fg"
      data-testid="task-grid"
      role="grid"
      aria-label="任務表格"
      tabIndex={0}
      onKeyDown={handleGridKeyDown}
      onPaste={handlePaste}
    >
      <div
        ref={headerRef}
        className="flex overflow-hidden border-b border-line-strong bg-raised font-medium"
        role="row"
      >
        <div
          role="columnheader"
          aria-label="排程模式"
          title="排程模式（圖釘 = 手動排程）"
          className="shrink-0 select-none border-r border-line px-2 py-1.5"
          style={{ width: SCHEDULE_GUTTER_WIDTH }}
        />
        {columns.map((column, colIndex) => (
          <div
            key={column.id}
            role="columnheader"
            className="relative flex shrink-0 select-none items-center border-r border-line px-2 py-1.5"
            style={{ width: column.width }}
          >
            <span className="truncate">{column.title}</span>
            <div
              data-testid={`resize-handle-${column.id}`}
              className="absolute right-0 top-0 h-full w-1.5 cursor-col-resize"
              onMouseDown={(event) => handleResizeMouseDown(event, colIndex)}
            />
          </div>
        ))}
      </div>

      <div
        ref={bodyRef}
        data-testid="task-grid-body"
        className="relative flex-1 overflow-auto"
        onScroll={handleBodyScroll}
        onContextMenu={(event) => openContextMenu(event, null)}
      >
        <div style={{ height: totalHeight, width: totalColumnsWidth, position: 'relative' }}>
          {renderedRows.map(({ task, rowIndex }) => {
            const isSelected = selectedSet.has(task.id);
            const isCritical = task.isCritical;
            return (
              <div
                key={task.id}
                role="row"
                data-testid={`grid-row-${task.id}`}
                data-manually-scheduled={task.isManuallyScheduled ? 'true' : 'false'}
                className={[
                  'absolute left-0 right-0 flex border-b border-hairline',
                  isSelected ? 'bg-blue-100 dark:bg-selected' : '',
                  task.isSummary ? 'font-bold' : '',
                ].join(' ')}
                style={{ top: rowIndex * safeRowHeight, height: safeRowHeight }}
                onContextMenu={(event) => {
                  event.stopPropagation();
                  openContextMenu(event, task);
                }}
              >
                <div
                  data-testid={`schedule-mode-${task.id}`}
                  title={task.isManuallyScheduled ? '手動排程' : undefined}
                  className="flex shrink-0 items-center justify-center border-r border-hairline text-fg-muted"
                  style={{ width: SCHEDULE_GUTTER_WIDTH }}
                >
                  {task.isManuallyScheduled ? <ManualScheduleMark /> : null}
                </div>
                {columns.map((column, colIndex) => {
                  const isActive = activeCell?.rowIndex === rowIndex && activeCell.colIndex === colIndex;
                  const isEditing = editing?.taskId === task.id && editing.columnId === column.id;
                  const customField = resolveCustomField(column, customFieldsById);
                  const indentPx = column.id === 'name' ? (task.outlineLevel - 1) * 16 : 0;
                  // 單一儲存格不畫範圍底色(只留作用格外框),避免和整列選取的底色打架。
                  const inRange =
                    selectionRange !== null &&
                    !isSingleCell(selectionRange) &&
                    isCellInRange(selectionRange, rowIndex, colIndex);

                  return (
                    <div
                      key={column.id}
                      role="gridcell"
                      data-testid={`cell-${task.id}-${column.id}`}
                      data-in-range={inRange ? 'true' : undefined}
                      className={[
                        'flex shrink-0 items-center overflow-hidden border-r border-hairline px-2',
                        inRange ? 'bg-blue-200/70 dark:bg-focus/25' : '',
                        isActive ? 'outline outline-2 outline-focus' : '',
                        isCritical && column.field === 'totalFloat' ? 'text-red-600 dark:text-danger' : '',
                        column.kind === 'progress-status'
                          ? progressStatusTextClass(expectedProgressByTask?.get(task.id)?.status ?? 'notStarted')
                          : '',
                      ].join(' ')}
                      style={{ width: column.width, paddingLeft: 8 + indentPx }}
                      onMouseDown={(event) => handleCellMouseDown(event, rowIndex, colIndex)}
                      onMouseEnter={() => handleCellMouseEnter(rowIndex, colIndex)}
                      onDoubleClick={() => startEditing(rowIndex, colIndex)}
                    >
                      {column.id === 'name' && task.isSummary ? (
                        <button
                          type="button"
                          aria-label={collapsedTaskIds.includes(task.id) ? '展開' : '收合'}
                          className="mr-1 w-3 shrink-0 text-fg-muted"
                          onClick={(event) => {
                            event.stopPropagation();
                            onToggleCollapse(task.id);
                          }}
                        >
                          {collapsedTaskIds.includes(task.id) ? '▶' : '▼'}
                        </button>
                      ) : null}

                      {isEditing ? (
                        <CellEditor
                          column={column}
                          customField={customField}
                          value={editing.draft}
                          onChange={(value) => setEditing((current) => (current ? { ...current, draft: value } : current))}
                          onCommit={() => commitEdit('down')}
                        />
                      ) : (
                        <>
                          <span className="truncate">
                            {cellDisplayText(task, column, dependencies, tasks, resourceNamesByTask, expectedProgressByTask)}
                          </span>
                          {column.kind === 'dependency-list' && isColumnEditable(column) ? (
                            <button
                              type="button"
                              aria-label={`選擇 ${task.name} 的前置任務`}
                              data-testid={`predecessor-picker-toggle-${task.id}`}
                              className="ml-auto shrink-0 px-1 text-fg-muted hover:text-focus"
                              onMouseDown={(event) => event.stopPropagation()}
                              onClick={(event) => {
                                event.stopPropagation();
                                openPicker(task.id, event.currentTarget.parentElement);
                              }}
                            >
                              ▾
                            </button>
                          ) : null}
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>

      {editError ? (
        <div
          role="alert"
          data-testid="grid-edit-error"
          className="absolute bottom-2 left-2 right-2 z-40 rounded-md border border-danger/40 bg-danger-soft px-3 py-1.5 text-xs text-danger shadow"
        >
          {editError}
        </div>
      ) : null}

      {picker ? (
        <PredecessorPicker
          key={picker.taskId}
          taskId={picker.taskId}
          tasks={tasks}
          dependencies={dependencies}
          top={picker.top}
          left={picker.left}
          error={pickerError}
          onCommit={(input) => commitPicker(picker.taskId, input)}
          onCancel={() => {
            setPicker(null);
            setPickerError(null);
          }}
        />
      ) : null}

      {contextMenu ? <ContextMenu state={contextMenu} onClose={closeMenus} /> : null}
      {showColumnSettings ? (
        <ColumnSettingsPanel
          columns={columns}
          availableColumns={availableColumns}
          onColumnsChange={onColumnsChange}
          onClose={() => setShowColumnSettings(false)}
        />
      ) : null}
    </div>
  );
}

interface CellEditorProps {
  readonly column: ColumnDef;
  readonly customField: (CustomFieldDefinition & { readonly kind: ManualTaskCustomFieldKind }) | null;
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly onCommit: () => void;
}

function CellEditor({ column, customField, value, onChange, onCommit }: CellEditorProps): React.JSX.Element {
  if (customField?.lookup !== null && customField?.lookup !== undefined) {
    return (
      <select
        autoFocus
        aria-label={customField.name}
        className="w-full bg-transparent outline-none"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        onBlur={onCommit}
      >
        <option value="">未設定</option>
        {customField.lookup
          .filter((item) => item.value !== null)
          .map((item) => (
            <option key={lookupOptionValue(item.value)} value={lookupOptionValue(item.value)}>
              {item.label}
            </option>
          ))}
      </select>
    );
  }

  if (customField?.kind === 'Flag') {
    return (
      <input
        type="checkbox"
        autoFocus
        aria-label={customField.name}
        checked={value === 'true'}
        onChange={(event) => onChange(event.target.checked ? 'true' : 'false')}
        onBlur={onCommit}
      />
    );
  }

  if (column.kind === 'date' || customField?.kind === 'Date') {
    // 刻意不用 <input type="date">:原生日期輸入的顯示格式跟著瀏覽器 locale 走
    // (常見為 mm/dd/yyyy),與表格其他地方統一的 yyyy-mm-dd 顯示格式不一致。
    return (
      <input
        type="text"
        autoFocus
        inputMode="numeric"
        placeholder="yyyy-mm-dd"
        aria-label={`${column.title}(yyyy-mm-dd)`}
        className="w-full bg-transparent outline-none"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        onBlur={onCommit}
        onFocus={(event) => event.currentTarget.select()}
      />
    );
  }

  const numeric = customField?.kind === 'Number' || customField?.kind === 'Cost';
  return (
    <input
      type={numeric ? 'number' : 'text'}
      autoFocus
      aria-label={customField?.name}
      inputMode={numeric ? 'decimal' : undefined}
      className="w-full bg-transparent outline-none"
      value={value}
      onChange={(event) => onChange(event.target.value)}
      onBlur={onCommit}
      onFocus={(event) => event.currentTarget.select()}
    />
  );
}
